/**
 * BootScreen — premium mobile-first boot
 * ---------------------------------------------------------------------------
 * Multi-color ambient glow, tighter mobile layout, refined typography.
 * Matrix rain stays but at lower opacity and with a wider colour range so
 * it reads as a subtle backdrop, not a green wash.
 */
import React, { useEffect, useRef, useState } from 'react';
import Logo from './Logo';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const BOOT_LINES = [
  '> boot sequence initiated',
  '> loading kernel · errorhacker-core',
  '> mounting /dev/secure-channel',
  '> decrypting user payload · AES-256',
  '> establishing mesh · c2.errorhacker.io',
];

const RAIN_COLORS = ['#00ff9d', '#00ff9d', '#00ff9d', '#4de0ff', '#c084fc'];

const MatrixRain = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
    };
    resize(); window.addEventListener('resize', resize);
    const chars = 'アァイウエオカガキクゲコサ0123456789ABCDEF#$%@!?<>/\\|';
    const fontSize = window.innerWidth < 640 ? 12 : 14;
    let drops = Array(Math.floor(window.innerWidth / fontSize)).fill(1);
    const draw = () => {
      ctx.fillStyle = 'rgba(4,6,7,0.09)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.font = `${fontSize}px "Share Tech Mono", monospace`;
      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillStyle = Math.random() > 0.98
          ? '#ffffff'
          : RAIN_COLORS[Math.floor(Math.random() * RAIN_COLORS.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > window.innerHeight && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 1;
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.22 }} />;
};

const BootScreen = ({ onDone }) => {
  const { config } = useSiteConfig();
  const SITE = config.site;
  const [lines, setLines] = useState([]);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let i = 0;
    let done = false;
    const finish = () => { if (done) return; done = true; clearInterval(id); clearTimeout(safety); onDone && onDone(); };
    const id = setInterval(() => {
      i += 1;
      setLines(prev => prev.length < BOOT_LINES.length ? [...prev, BOOT_LINES[prev.length]] : prev);
      setProgress(Math.min(100, Math.round((i / BOOT_LINES.length) * 100)));
      if (i >= BOOT_LINES.length) { clearInterval(id); setTimeout(finish, 650); }
    }, 360);
    // Mobile safety net: dismiss after 4s regardless
    const safety = setTimeout(finish, 4000);
    return () => { done = true; clearInterval(id); clearTimeout(safety); };
  }, [onDone]);

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden" style={{ background: '#050706' }}>
      <MatrixRain />

      {/* Multi-color ambient glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `
          radial-gradient(45% 40% at 50% 42%, rgba(0,255,157,0.14), transparent 70%),
          radial-gradient(30% 30% at 15% 85%, rgba(77,224,255,0.08), transparent 70%),
          radial-gradient(30% 30% at 85% 15%, rgba(192,132,252,0.06), transparent 70%)
        `,
      }} />

      {/* Scanlines */}
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        background: 'repeating-linear-gradient(0deg, rgba(0,255,157,.045) 0 1px, transparent 1px 3px)',
      }} />

      {/* Top progress line */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'rgba(0,255,157,.10)' }}>
        <div className="h-full transition-all duration-300" style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #00ff9d, #4de0ff, #c084fc)',
          boxShadow: '0 0 8px #00ff9d',
        }} />
      </div>

      <div className="relative h-full w-full flex items-center justify-center px-4 py-6">
        <div
          className="boot-card w-full max-w-[400px] sm:max-w-[440px] rounded-2xl border-2 relative overflow-hidden"
          style={{
            background: 'rgba(6, 9, 8, 0.86)',
            backdropFilter: 'blur(18px) saturate(140%)',
            WebkitBackdropFilter: 'blur(18px) saturate(140%)',
            borderColor: 'rgba(0, 255, 157, 0.35)',
            boxShadow: '0 40px 80px -30px rgba(0, 255, 157, 0.35), 0 0 0 1px rgba(0, 255, 157, 0.08) inset',
          }}
        >
          {/* Accent stripe */}
          <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #00ff9d, #4de0ff, #c084fc, transparent)' }} />

          <div className="px-6 pt-5 pb-6 sm:px-7 sm:pt-6 sm:pb-7">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2 eh-mono text-[10px] tracking-widest opacity-85" style={{ color: '#00ff9d' }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#00ff9d', boxShadow: '0 0 6px #00ff9d' }} />
                errorhacker_boot.sh
              </div>
              <div className="flex items-center gap-2.5">
                <span className="eh-mono text-[10px] tracking-widest opacity-55">{SITE.version}</span>
                <button
                  onClick={() => onDone && onDone()}
                  className="eh-mono text-[9.5px] tracking-widest px-2 py-1 rounded border transition-colors"
                  style={{ borderColor: 'rgba(0,255,157,.35)', color: '#00ff9d' }}
                  aria-label="Skip boot"
                >
                  SKIP
                </button>
              </div>
            </div>

            {/* Logo + halo */}
            <div className="flex justify-center mb-4 sm:mb-5">
              <div className="relative w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] flex items-center justify-center">
                {/* Rotating ring */}
                <svg className="absolute inset-0 w-full h-full eh-spin-slow" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="47" stroke="rgba(0,255,157,.12)" strokeWidth="0.8" />
                  <circle cx="50" cy="50" r="47" stroke="#00ff9d" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="60 240" />
                </svg>
                {/* Inner pulse */}
                <div className="absolute inset-2 rounded-full" style={{
                  background: 'radial-gradient(circle, rgba(0,255,157,.18) 0%, transparent 70%)',
                  animation: 'boot-pulse 2s ease-in-out infinite',
                }} />
                <div className="relative z-10 rounded-full overflow-hidden" style={{ boxShadow: '0 0 24px rgba(0,255,157,.4)' }}>
                  <Logo size={54} />
                </div>
              </div>
            </div>

            {/* Brand */}
            <h1
              className="text-center font-black tracking-[0.10em] mb-2"
              style={{
                fontSize: 'clamp(22px, 5vw, 30px)',
                fontFamily: '"Cinzel", serif',
                background: 'linear-gradient(180deg, #00ff9d, #00c67c)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 12px rgba(0,255,157,.35))',
              }}
            >
              {SITE.name}
            </h1>

            {/* Divider */}
            <div className="mx-auto w-16 h-px mb-3" style={{
              background: 'linear-gradient(90deg, transparent, #00ff9d, transparent)',
              boxShadow: '0 0 6px #00ff9d',
            }} />

            {/* Subtitle */}
            <div className="text-center mb-5 eh-mono text-[10.5px] tracking-[0.32em] opacity-75" style={{ color: '#7cffcc' }}>
              INITIALIZING SYSTEM
            </div>

            {/* Terminal log */}
            <div className="rounded-lg border p-3 sm:p-3.5 min-h-[110px]" style={{
              background: 'rgba(0, 0, 0, 0.35)',
              borderColor: 'rgba(0,255,157,.14)',
            }}>
              {lines.map((l, i) => (
                <div
                  key={i}
                  className="eh-mono text-[11px] sm:text-[11.5px] leading-[1.7]"
                  style={{
                    color: i === lines.length - 1 ? '#7cffcc' : 'rgba(0, 255, 157, 0.55)',
                    animation: i === lines.length - 1 ? 'boot-line-in .3s ease-out' : 'none',
                  }}
                >
                  {l}
                </div>
              ))}
              <span className="eh-caret eh-mono text-[11px]" style={{ color: '#00ff9d' }}>&nbsp;</span>
            </div>

            {/* Progress percent */}
            <div className="mt-4 flex items-center justify-between eh-mono text-[10px] tracking-widest opacity-70">
              <span>PROGRESS</span>
              <span style={{ color: '#00ff9d' }}>{progress}%</span>
            </div>
            <div className="mt-1.5 h-[3px] rounded-full overflow-hidden" style={{ background: 'rgba(0,255,157,.10)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #00ff9d, #4de0ff)',
                  boxShadow: '0 0 6px #00ff9d',
                }}
              />
            </div>
          </div>

          {/* Corner tickers */}
          <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,157,.4), transparent)' }} />
        </div>
      </div>

      <style>{`
        @keyframes boot-pulse   { 0%,100% { opacity: .5; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
        @keyframes boot-line-in { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default BootScreen;
